import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, F } from "@/pages/settings/AdminTabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, todayIso } from "@/lib/api";

export function RoomsTab({ admin }) {
  const [rooms, setRooms] = useState([]);
  const [n, setN] = useState({ name: "", capacity: 2, ical_booking: "", ical_airbnb: "", active: true });
  const load = () => api.get("/rooms").then(({ data }) => setRooms(data));
  useEffect(() => {
    load();
  }, []);
  const save = async (r) => {
    try {
      await api.put(`/rooms/${r.id}`, { ...r, capacity: Number(r.capacity) });
      toast.success("Camera salvata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const add = async () => {
    try {
      await api.post("/rooms", { ...n, capacity: Number(n.capacity) });
      setN({ name: "", capacity: 2, ical_booking: "", ical_airbnb: "", active: true });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const [toDelete, setToDelete] = useState(null);
  const remove = async () => {
    try {
      await api.delete(`/rooms/${toDelete.id}`);
      toast.success("Camera eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };
  const sync = async (r) => {
    try {
      const { data } = await api.post(`/ical/sync/${r.id}`);
      toast.success(`Sincronizzate: ${data.created} nuove prenotazioni${data.blocked ? ` (${data.blocked} date bloccate Airbnb ignorate)` : ""}`);
      if (data.errors?.length) toast.error(`Calendario ${data.errors.join(", ")} non scaricato: verifica l'indirizzo`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const upd = (id, patch) => setRooms(rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const active = rooms.filter((r) => r.active);
  return (
    <Card title="Camere" desc={`${active.length} camere attive · ${active.reduce((s, r) => s + Number(r.capacity), 0)} posti letto totali`}>
      <div className="space-y-3">
        {rooms.map((r) => (
          <div key={r.id} className="rounded-xl border p-3 space-y-3" data-testid={`room-row-${r.id}`}>
            <div className="grid gap-3 sm:grid-cols-[1fr_100px_auto] items-end">
              <F label="Nome"><Input value={r.name} disabled={!admin} onChange={(e) => upd(r.id, { name: e.target.value })} data-testid={`room-name-${r.id}`} /></F>
              <F label="Posti letto"><Input type="number" min={1} value={r.capacity} disabled={!admin} onChange={(e) => upd(r.id, { capacity: e.target.value })} /></F>
              <label className="flex items-center gap-2 text-sm pb-2">Attiva<Switch checked={r.active} disabled={!admin} onCheckedChange={(v) => upd(r.id, { active: v })} /></label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <F label="Calendario iCal Booking.com"><Input value={r.ical_booking || ""} disabled={!admin} placeholder="https://admin.booking.com/…/ical…" onChange={(e) => upd(r.id, { ical_booking: e.target.value.trim() })} data-testid={`room-ical-booking-${r.id}`} /></F>
              <F label="Calendario iCal Airbnb"><Input value={r.ical_airbnb || ""} disabled={!admin} placeholder="https://www.airbnb.it/calendar/ical/…" onChange={(e) => upd(r.id, { ical_airbnb: e.target.value.trim() })} data-testid={`room-ical-airbnb-${r.id}`} /></F>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {(r.ical_booking || r.ical_airbnb) && <Button variant="outline" size="sm" onClick={() => sync(r)} data-testid={`room-sync-${r.id}`}><RefreshCw className="h-4 w-4 mr-1" />Sincronizza ora</Button>}
              {admin && <Button variant="ghost" size="sm" className="text-rose-700" onClick={() => setToDelete(r)} data-testid={`room-delete-${r.id}`}><Trash2 className="h-4 w-4 mr-1" />Elimina</Button>}
              {admin && <Button size="sm" onClick={() => save(r)} data-testid={`room-save-${r.id}`}>Salva</Button>}
            </div>
          </div>
        ))}
      </div>
      {admin && (
        <div className="grid gap-3 sm:grid-cols-[1fr_100px_auto] items-end border-t pt-4">
          <F label="Nuova camera"><Input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="new-room-name-input" /></F>
          <F label="Posti letto"><Input type="number" min={1} value={n.capacity} onChange={(e) => setN({ ...n, capacity: e.target.value })} data-testid="new-room-capacity-input" /></F>
          <Button onClick={add} disabled={!n.name} data-testid="add-room-button"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
        </div>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="room-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Si può eliminare solo una camera senza prenotazioni (tranne quelle cancellate). Se ha soggiorni passati, disattivala: servono per lo storico ISTAT.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="room-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-700 hover:bg-rose-800" data-testid="room-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export function ClosuresTab({ admin }) {
  const [ym, setYm] = useState(todayIso().slice(0, 7));
  const [closed, setClosed] = useState([]);
  const year = ym.slice(0, 4);
  const load = () => api.get("/closures", { params: { year } }).then(({ data }) => setClosed(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);
  const n = new Date(+year, +ym.slice(5, 7), 0).getDate();
  const offset = (new Date(+year, +ym.slice(5, 7) - 1, 1).getDay() + 6) % 7;
  const days = Array.from({ length: n }, (_, i) => `${ym}-${String(i + 1).padStart(2, "0")}`);
  const toggle = async (d) => {
    if (!admin) return;
    try {
      await api.post("/closures", { dates: [d], closed: !closed.includes(d) });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shift = (m) => {
    const d = new Date(+year, +ym.slice(5, 7) - 1 + m, 1);
    setYm(d.toLocaleDateString("sv-SE").slice(0, 7));
  };
  return (
    <Card title="Giorni di chiusura dell'esercizio" desc="Tocca un giorno per segnarlo chiuso/aperto. Nei giorni chiusi la movimentazione ISTAT riporta 'Esercizio chiuso'.">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="closures-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="min-w-[140px] text-center font-semibold capitalize">{new Date(ym + "-15").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</span>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="closures-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 max-w-md">
        {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => <div key={i} className="text-center text-xs font-semibold text-stone-400">{d}</div>)}
        {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
        {days.map((d) => (
          <button key={d} onClick={() => toggle(d)} data-testid={`closure-day-${d}`}
            className={`h-11 rounded-lg text-sm font-semibold transition-colors ${closed.includes(d) ? "bg-stone-800 text-white" : "bg-stone-100 hover:bg-stone-200"}`}>
            {+d.slice(8)}
          </button>
        ))}
      </div>
    </Card>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [n, setN] = useState({ email: "", name: "", role: "operatore", password: "" });
  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => {
    load();
  }, []);
  const add = async () => {
    try {
      await api.post("/users", n);
      toast.success("Utente creato");
      setN({ email: "", name: "", role: "operatore", password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { ...u, active: !u.active, password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Utenti" desc="Gli operatori gestiscono prenotazioni e check-in ma non vedono credenziali e configurazione.">
      <ul className="divide-y">
        {users.map((u) => (
          <li key={u.id} className="flex items-center justify-between py-3" data-testid={`user-row-${u.email}`}>
            <div><p className="font-semibold text-sm">{u.name} <span className="text-xs text-stone-500">· {u.role}{u.totp_enabled ? " · 2FA" : ""}</span></p><p className="text-xs text-stone-500">{u.email}</p></div>
            <label className="flex items-center gap-2 text-xs">Attivo<Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`user-active-${u.email}`} /></label>
          </li>
        ))}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end border-t pt-4">
        <F label="Nome"><Input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="new-user-name-input" /></F>
        <F label="Email"><Input type="email" value={n.email} onChange={(e) => setN({ ...n, email: e.target.value })} data-testid="new-user-email-input" /></F>
        <F label="Ruolo">
          <Select value={n.role} onValueChange={(v) => setN({ ...n, role: v })}>
            <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="operatore">Operatore</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </F>
        <F label="Password (min 10)"><Input type="password" value={n.password} onChange={(e) => setN({ ...n, password: e.target.value })} data-testid="new-user-password-input" /></F>
        <Button onClick={add} data-testid="add-user-button">Crea utente</Button>
      </div>
    </Card>
  );
}

export function SecurityTab() {
  const { user, refresh } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const changePw = async () => {
    try {
      await api.post("/auth/password", pw);
      toast.success("Password aggiornata");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const start = async () => setSetup((await api.post("/auth/2fa/setup")).data);
  const enable = async () => {
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Verifica in due passaggi attivata");
      setSetup(null);
      setCode("");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const disable = async () => {
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Verifica in due passaggi disattivata");
      setCode("");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <Card title="Cambia password">
        <div className="grid gap-3 sm:grid-cols-3 items-end">
          <F label="Password attuale"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></F>
          <F label="Nuova password (min 10)"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" /></F>
          <Button onClick={changePw} data-testid="change-password-button">Aggiorna</Button>
        </div>
      </Card>
      {user?.role === "admin" && (
        <Card title="Verifica in due passaggi (2FA)" desc="Con un'app di autenticazione (Google Authenticator, Authy, 1Password…).">
          <p className="text-sm" data-testid="2fa-status">Stato: <b>{user.totp_enabled ? "attiva" : "non attiva"}</b></p>
          {!user.totp_enabled && !setup && <Button variant="outline" onClick={start} data-testid="2fa-setup-button">Attiva 2FA</Button>}
          {setup && (
            <div className="space-y-3">
              <QRCodeSVG value={setup.uri} size={168} />
              <p className="text-xs text-stone-500">Oppure inserisci la chiave: <code className="font-mono" data-testid="2fa-secret">{setup.secret}</code></p>
            </div>
          )}
          {(setup || user.totp_enabled) && (
            <div className="flex gap-2 max-w-sm">
              <Input inputMode="numeric" placeholder="Codice a 6 cifre" value={code} onChange={(e) => setCode(e.target.value)} data-testid="2fa-code-input" />
              {setup ? <Button onClick={enable} data-testid="2fa-enable-button">Attiva</Button> : <Button variant="outline" onClick={disable} data-testid="2fa-disable-button">Disattiva</Button>}
            </div>
          )}
        </Card>
      )}
    </>
  );
}
